import type { User } from "@supabase/supabase-js"
import type { EmailGroupT } from "~~/types"

export const useUserStore = defineStore("user", () => {
    const user = ref<User | null>(null)
    const loading = ref(true)
    const organizationsStore = useOrganizationsStore()
    const supabase = useSupabaseClient()
    const userProfile = ref<any>(null)
    const userOrganizations = ref<EmailGroupT[]>([])
    const userOrgRoles = ref<Record<string, string>>({})

    const isLoggedIn = computed(() => !!user.value)
    const isAdmin = computed(() => userProfile.value?.is_admin || false)

    // Filter organizations that the user has access to
    const accessibleOrganizations = computed(() => {
        if (!user.value) return []
        if (isAdmin.value) return organizationsStore.items
        return userOrganizations.value
    })

    // Track auth state changes
    async function init() {
        loading.value = true

        // Get initial session
        const {data} = await supabase.auth.getSession()
        user.value = data.session?.user || null

        // Set up auth state change listener
        supabase.auth.onAuthStateChange((event, session) => {
            user.value = session?.user || null
            if (event === "SIGNED_IN") {
                fetchUserProfile()
                fetchUserOrganizations()
                // Both stores load once, when first created. On the login page that
                // is before sign-in, so the request fails and they stay empty.
                // SIGNED_IN also fires on token refresh, so only fill what is empty.
                if (!organizationsStore.items.length) organizationsStore.fetchUserOrganizations()
                const emailsStore = useEmailsStore()
                if (!emailsStore.items.length) emailsStore.fetchEmails().catch(() => {})
            } else if (event === "SIGNED_OUT") {
                userProfile.value = null
                userOrganizations.value = []
                userOrgRoles.value = {}
            }
        })

        if (user.value) {
            await fetchUserProfile()
            await fetchUserOrganizations()
            // The organizations store skips its own fetch when it is created before the
            // session is known. Fill it here if that happened.
            if (!organizationsStore.items.length && !organizationsStore.loading) organizationsStore.fetchUserOrganizations()
            const emailsStore = useEmailsStore()
            if (!emailsStore.items.length && !emailsStore.loading) emailsStore.fetchEmails().catch(() => {})
        }

        loading.value = false
    }

    // Fetch user profile from public schema
    async function fetchUserProfile() {
        if (!user.value) return

        try {
            const {data, error} = await supabase
               .from("user_profiles")
               .select("*, app_roles(name)")
               .eq("id", user.value.id)
               .single()

            if (error) throw error

            userProfile.value = data
        } catch (error) {
            console.error("Error fetching user profile:", error)
            userProfile.value = null
        }
    }

    // Fetch organizations that the user has access to
    async function fetchUserOrganizations() {
        if (!user.value) return

        try {
            // First fetch user organizations with role IDs instead of using app_roles relationship
            const {data, error} = await supabase
               .from("user_organizations")
               .select("organization_alias, organization_id, role_id")
               .eq("user_id", user.value.id)

            if (error) throw error

            if (!data || data.length === 0) {
                userOrganizations.value = []
                userOrgRoles.value = {}
                return
            }

            // Fetch role names separately
            const roleIds = [...new Set(data.map(item => item.role_id))].filter(Boolean)
            if (roleIds.length > 0) {
                const {data: roleData, error: roleError} = await supabase
                   .from("app_roles")
                   .select("id, name")
                   .in("id", roleIds)

                if (!roleError && roleData) {
                    // Create a map of role IDs to names
                    const roleMap: Record<number, string> = {}
                    roleData.forEach(role => {
                        roleMap[role.id] = role.name
                    })

                    // Store organization roles by alias
                    userOrgRoles.value = data.reduce((acc: Record<string, string>, item: any) => {
                        acc[item.organization_alias] = roleMap[item.role_id] || "viewer" // Default to viewer
                        return acc
                    }, {})
                }
            }

            // Fetch organizations directly since we have the IDs
            const orgIds = data.map(item => item.organization_id).filter(Boolean)
            if (orgIds.length > 0) {
                const {data: orgData, error: orgError} = await supabase
                   .from("organizations")
                   .select("*")
                   .in("id", orgIds)

                if (!orgError && orgData) {
                    // Update the organizations store with any new data
                    orgData.forEach(org => {
                        if (org.alias && !organizationsStore.map[org.alias]) {
                            organizationsStore.map[org.alias] = org
                        }
                    })

                    // Map to organization objects
                    userOrganizations.value = data
                       .map(item => {
                           // Try by ID first, then fall back to alias for backward compatibility
                           const org = orgData.find(o => o.id === item.organization_id)
                           return org || organizationsStore.map[item.organization_alias]
                       })
                       .filter(Boolean)
                } else {
                    // Fallback to using just aliases if org query fails
                    userOrganizations.value = data
                       .map(item => organizationsStore.map[item.organization_alias])
                       .filter(Boolean)
                }
            } else {
                // Fallback to using just aliases
                userOrganizations.value = data
                   .map(item => organizationsStore.map[item.organization_alias])
                   .filter(Boolean)
            }
        } catch (error) {
            console.error("Error fetching user organizations:", error)
            userOrganizations.value = []
            userOrgRoles.value = {}
        }
    }

    // Get user's role for a specific organization
    function getUserRoleForOrg(orgAlias: string): string {
        if (isAdmin.value) return "admin"
        return userOrgRoles.value[orgAlias] || "none"
    }

    // Check if user can edit in an organization
    function canEditOrg(orgAlias: string): boolean {
        if (isAdmin.value) return true
        const role = getUserRoleForOrg(orgAlias)
        return role === "admin" || role === "editor"
    }

    // Sign in with email and password
    async function signIn(email: string, password: string) {
        try {
            const {data, error} = await supabase.auth.signInWithPassword({
                email,
                password
            })

            if (error) throw error
            return {success: true, user: data.user}
        } catch (error) {
            console.error("Error signing in:", error)
            return {
                success: false,
                error: error instanceof Error ? error.message : "An unknown error occurred"
            }
        }
    }

    // Sign out
    async function signOut() {
        try {
            const {error} = await supabase.auth.signOut()
            if (error) throw error
            return {success: true}
        } catch (error) {
            console.error("Error signing out:", error)
            return {
                success: false,
                error: error instanceof Error ? error.message : "An unknown error occurred"
            }
        }
    }

    // Reset password
    async function resetPassword(email: string) {
        try {
            const {error} = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: `${window.location.origin}/reset-password`
            })

            if (error) throw error
            return {success: true}
        } catch (error) {
            console.error("Error resetting password:", error)
            return {
                success: false,
                error: error instanceof Error ? error.message : "An unknown error occurred"
            }
        }
    }

    // Initialize on store creation
    init()

    return {
        user,
        userProfile,
        loading,
        isLoggedIn,
        isAdmin,
        accessibleOrganizations,
        userOrganizations,
        getUserRoleForOrg,
        canEditOrg,
        signIn,
        signOut,
        resetPassword,
        fetchUserProfile,
        fetchUserOrganizations
    }
})
